/**
 * /onboarding/wizard — Wizard 5 pasos para que un Facturador Final
 * configure todo lo necesario antes de emitir.
 *
 * Pasos:
 *   1. Datos básicos (legal_name, NIT-DV, dirección, ciudad, depto, CIIU)
 *   2. Datos fiscales (régimen, responsabilidades fiscales)
 *   3. Cert ECD: mandato Taxor (default) vs BYO upload .p12
 *   4. Resolución DIAN (número, prefix, rango, validez). La CLAVE TÉCNICA
 *      NO se pide: la gestiona Taxor como PT (la consulta a DIAN vía
 *      GetNumberingRange y la inyecta server-side — md/009).
 *   5. Confirmación → PUT facturador-profile + complete-onboarding
 *
 * Estado vive en el componente padre. Solo se persiste al backend en
 * el paso 5 (un único PUT atomicamente). Si el usuario cierra el
 * navegador en pasos 1-4, no hay fila parcial en DB.
 *
 * Excepción: el upload BYO cert (paso 3) sí se persiste inmediatamente
 * via /cert/upload — es un archivo binario y tiene su propio endpoint.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, Building2, Check, ChevronLeft, ChevronRight, KeyRound, Loader2, ShieldCheck, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  certApi,
  profileApi,
  type NumberingRange,
  type NumberingRangesResult,
  type UpsertProfileRequest,
} from '@/lib/api'
import {
  DEPARTMENTS,
  FISCAL_RESPONSIBILITIES,
  ROUTES,
  TAX_REGIMES,
} from '@/lib/constants'

type CertChoice = 'mandate' | 'byo'

interface WizardState {
  // Paso 1
  legal_name: string
  trade_name: string
  nit_verification_digit: string
  address: string
  city: string
  department: string
  country: string
  ciiu: string
  // Paso 2
  fiscal_responsibilities: string[]
  tax_regime: string
  // Paso 3
  cert_choice: CertChoice
  cert_uploaded: boolean
  // Paso 4
  resolution_number: string
  resolution_prefix: string
  resolution_from: string
  resolution_to: string
  resolution_valid_from: string
  resolution_valid_to: string
  software_id: string
}

const STEPS = [
  { n: 1, label: 'Datos básicos' },
  { n: 2, label: 'Datos fiscales' },
  { n: 3, label: 'Certificado ECD' },
  { n: 4, label: 'Resolución DIAN' },
  { n: 5, label: 'Confirmación' },
] as const

const DEFAULT_STATE: WizardState = {
  legal_name: '',
  trade_name: '',
  nit_verification_digit: '',
  address: '',
  city: '',
  department: '',
  country: 'CO',
  ciiu: '',
  fiscal_responsibilities: [],
  tax_regime: '',
  cert_choice: 'mandate',
  cert_uploaded: false,
  resolution_number: '',
  resolution_prefix: '',
  resolution_from: '',
  resolution_to: '',
  resolution_valid_from: '',
  resolution_valid_to: '',
  software_id: '',
}

export function WizardPage() {
  const nav = useNavigate()
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const [step, setStep] = useState(1)
  const [s, setS] = useState<WizardState>(DEFAULT_STATE)

  // Snapshot autoritativo desde RUES (Cámara de Comercio). Es la
  // fuente única de verdad para datos del facturador: legal_name,
  // NIT, DV (calculado por algoritmo DIAN, NO input), dirección,
  // departamento/municipio, CIIU resueltos, rep legal, etc.
  //
  // Se carga PRIMERO (antes del prefill desde profile draft) porque
  // RUES es autoritativo: si el usuario actualizó datos en Cámara,
  // el wizard debe reflejar el cambio aunque el draft del profile
  // tenga valores stale.
  //
  // El campo `source` discrimina el resultado:
  //   - 'rues':   snapshot completo, campos van read-only.
  //   - 'manual': NIT no encontrado en Cámara — captura manual.
  //   - 'error':  RUES no respondió — captura manual + banner.
  const ruesSnap = useQuery({
    queryKey: ['profile-from-rues', activeOrgId],
    queryFn: () => profileApi.fromRues(activeOrgId!),
    enabled: !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Whitelist read-only para StepBasics. Hasta que llegue el snapshot,
  // se asume que el DV (calculado) y country son read-only por defecto.
  const ruesFilled = new Set<string>(
    ruesSnap.data?.filled_fields ?? ['nit_verification_digit', 'country'],
  )

  // Pre-cargar profile draft si existe (wizard puede re-entrar).
  // Política de prefill:
  //   - Para campos en ruesFilled: SIEMPRE gana el snapshot RUES
  //     (fuente única — un draft con dirección vieja no debe pisar
  //     la dirección actual de Cámara).
  //   - Para campos ausentes de ruesFilled (captura manual o no
  //     reportados): gana el draft si existe, si no queda vacío.
  useQuery({
    queryKey: ['profile-prefill', activeOrgId, ruesSnap.data?.tax_client_id],
    queryFn: async () => {
      const snap = ruesSnap.data
      try {
        const p = await profileApi.get(activeOrgId!)
        setS((prev) => ({
          ...prev,
          // Campos RUES: snapshot gana siempre cuando está poblado.
          legal_name: snap?.legal_name || p.legal_name,
          trade_name: snap?.trade_name || (p.trade_name ?? ''),
          nit_verification_digit:
            snap?.nit_verification_digit || p.nit_verification_digit,
          address: snap?.address || p.address,
          country: snap?.country || p.country,
          ciiu: snap?.ciiu || p.ciiu,
          // city/department: RUES los reporta como `municipality`/`department`
          // textual; si vienen, ganan. Si no, profile draft.
          city: snap?.municipality || p.city,
          department: snap?.department || p.department,
          // Campos solo-profile (no en RUES).
          fiscal_responsibilities: p.fiscal_responsibilities,
          tax_regime: p.tax_regime,
          cert_choice: 'mandate',
          cert_uploaded: false,
          resolution_number: p.resolution_number,
          resolution_prefix: p.resolution_prefix,
          resolution_from: String(p.resolution_from),
          resolution_to: String(p.resolution_to),
          resolution_valid_from: p.resolution_valid_from?.slice(0, 10) ?? '',
          resolution_valid_to: p.resolution_valid_to?.slice(0, 10) ?? '',
          software_id: p.software_id ?? '',
        }))
        return p
      } catch {
        // No hay profile draft — solo prefillar desde RUES snapshot.
        if (snap) {
          setS((prev) => ({
            ...prev,
            legal_name: snap.legal_name || prev.legal_name,
            trade_name: snap.trade_name || prev.trade_name,
            nit_verification_digit:
              snap.nit_verification_digit || prev.nit_verification_digit,
            address: snap.address || prev.address,
            country: snap.country || prev.country,
            ciiu: snap.ciiu || prev.ciiu,
            city: snap.municipality || prev.city,
            department: snap.department || prev.department,
          }))
        }
        return null
      }
    },
    enabled: !!activeOrgId && ruesSnap.isFetched,
    staleTime: 5 * 60 * 1000,
  })

  // Rangos de numeración detectados vía Taxor como PT. La clave técnica
  // es Taxor-managed (md/009): el backend la consulta a DIAN y la inyecta
  // al guardar — el usuario nunca la ve ni la digita. En hab llega la
  // resolución de prueba del SIE DIAN.
  const ranges = useQuery({
    queryKey: ['numbering-ranges', activeOrgId],
    queryFn: () => profileApi.numberingRanges(activeOrgId!),
    enabled: !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Auto-aplicar el rango vigente si el paso 4 sigue vacío (no pisa
  // capturas del usuario ni el draft del profile).
  useEffect(() => {
    const detected =
      ranges.data?.ranges.find((r) => r.is_active) ?? ranges.data?.ranges[0]
    if (!detected) return
    setS((prev) => {
      if (prev.resolution_number) return prev
      return applyRangeToState(prev, detected)
    })
  }, [ranges.data])

  const submit = useMutation({
    mutationFn: async () => {
      const body: UpsertProfileRequest = {
        legal_name: s.legal_name,
        trade_name: s.trade_name || undefined,
        nit_verification_digit: s.nit_verification_digit,
        address: s.address,
        city: s.city,
        department: s.department,
        country: s.country,
        ciiu: s.ciiu,
        fiscal_responsibilities: s.fiscal_responsibilities,
        tax_regime: s.tax_regime,
        resolution_number: s.resolution_number,
        resolution_prefix: s.resolution_prefix,
        resolution_from: parseInt(s.resolution_from, 10),
        resolution_to: parseInt(s.resolution_to, 10),
        resolution_valid_from: s.resolution_valid_from,
        resolution_valid_to: s.resolution_valid_to,
        // resolution_technical_key se omite a propósito: la resuelve el
        // backend (Taxor-managed, md/009).
        software_id: s.software_id || undefined,
      }
      await profileApi.upsert(activeOrgId!, body)
      await profileApi.completeOnboarding(activeOrgId!)
    },
    onSuccess: () => {
      toast.success('Onboarding completo. Ya puedes emitir facturas.')
      nav(ROUTES.DASHBOARD)
    },
    onError: (err: unknown) => {
      // Surfacea el mensaje del backend (p.ej. range_source_unavailable /
      // resolution_not_found al resolver la clave técnica server-side).
      const e = err as {
        response?: { data?: { message?: string; error?: string } }
        message?: string
      }
      const msg =
        e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? 'error'
      toast.error(`No se pudo completar: ${msg}`)
    },
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  const next = () => setStep((x) => Math.min(STEPS.length, x + 1))
  const prev = () => setStep((x) => Math.max(1, x - 1))
  const canAdvance = validateStep(step, s)

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Configura tu facturador</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          5 pasos para que puedas emitir facturas electrónicas DIAN.
        </p>

        <Stepper current={step} />

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          {step === 1 && (
            <StepBasics
              s={s}
              setS={setS}
              ruesFilled={ruesFilled}
              ruesSource={ruesSnap.data?.source}
            />
          )}
          {step === 2 && <StepFiscal s={s} setS={setS} />}
          {step === 3 && <StepCert s={s} setS={setS} activeOrgId={activeOrgId} />}
          {step === 4 && (
            <StepResolution
              s={s}
              setS={setS}
              ranges={ranges.data}
              rangesResolved={ranges.isSuccess || ranges.isError}
              rangesError={ranges.isError}
            />
          )}
          {step === 5 && <StepConfirm s={s} />}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={prev}
            disabled={step === 1}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface px-4 py-2 text-sm font-medium text-fg-primary hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" /> Anterior
          </button>

          {step < STEPS.length ? (
            <button
              type="button"
              onClick={next}
              disabled={!canAdvance}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Siguiente <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => submit.mutate()}
              disabled={submit.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                </>
              ) : (
                <>
                  Confirmar y activar <Check className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  )
}

// ─── Stepper visual ─────────────────────────────────────────────────

function Stepper({ current }: { current: number }) {
  return (
    <ol className="mt-6 flex items-center gap-2 overflow-x-auto">
      {STEPS.map((st, i) => {
        const done = current > st.n
        const active = current === st.n
        return (
          <li key={st.n} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold ${
                done
                  ? 'bg-success text-white'
                  : active
                  ? 'bg-brand-600 text-white'
                  : 'bg-bg-elevated text-fg-muted'
              }`}
            >
              {done ? <Check className="h-4 w-4" /> : st.n}
            </div>
            <span
              className={`whitespace-nowrap text-xs font-medium ${
                active ? 'text-fg-primary' : 'text-fg-muted'
              }`}
            >
              {st.label}
            </span>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
          </li>
        )
      })}
    </ol>
  )
}

// ─── Validación per-step ────────────────────────────────────────────

function validateStep(step: number, s: WizardState): boolean {
  switch (step) {
    case 1:
      return !!(
        s.legal_name &&
        /^\d$/.test(s.nit_verification_digit) &&
        s.address &&
        s.city &&
        s.department &&
        /^\d{4,6}$/.test(s.ciiu)
      )
    case 2:
      return s.fiscal_responsibilities.length > 0 && !!s.tax_regime
    case 3:
      // BYO requiere upload exitoso. Mandato siempre ok.
      return s.cert_choice === 'mandate' || s.cert_uploaded
    case 4:
      // La clave técnica NO se valida aquí: es Taxor-managed (md/009),
      // el backend la resuelve al guardar.
      const from = parseInt(s.resolution_from, 10)
      const to = parseInt(s.resolution_to, 10)
      return !!(
        s.resolution_number &&
        s.resolution_prefix &&
        from > 0 &&
        to > from &&
        s.resolution_valid_from &&
        s.resolution_valid_to
      )
    case 5:
      return true
    default:
      return false
  }
}

// ─── Step 1: Datos básicos ──────────────────────────────────────────

function StepBasics({
  s,
  setS,
  ruesFilled,
  ruesSource,
}: {
  s: WizardState
  setS: React.Dispatch<React.SetStateAction<WizardState>>
  /** Set de keys que YA vienen de RUES — esos inputs van disabled +
   *  badge "Desde RUES". El DV y el country están SIEMPRE aquí. */
  ruesFilled: Set<string>
  /** Discriminator del lookup RUES: 'rues' (OK), 'manual' (NIT no en
   *  Cámara), 'error' (RUES caído). Undefined mientras carga. */
  ruesSource?: 'rues' | 'manual' | 'error'
}) {
  const fromRues = (key: string) => ruesFilled.has(key)

  // El DV es matemática pura del NIT (algoritmo DIAN). Nunca es input
  // del usuario — incluso si RUES está caído, el backend lo calcula
  // y lo devuelve en el snapshot. Por eso `nit_verification_digit`
  // SIEMPRE va disabled.
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Datos básicos del facturador</h2>
      <p className="text-sm text-fg-secondary">
        Esta información va a tus facturas como emisor (cabecera del XML UBL).
        Los campos con candado vienen de Cámara de Comercio vía RUES — si
        necesitas cambiarlos, actualízalos en Cámara.
      </p>

      {/* Banner según el resultado del lookup RUES. */}
      {ruesSource === 'manual' && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <strong>NIT no encontrado en RUES.</strong> Captura los datos
          manualmente. Si la empresa es nueva, espera 24-72h después del
          registro en Cámara para que aparezca.
        </div>
      )}
      {ruesSource === 'error' && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
          <strong>RUES no respondió.</strong> Captura los datos manualmente
          por ahora. Cuando el servicio vuelva, podrás re-sincronizar desde
          tu perfil de empresa.
        </div>
      )}

      <Field label="Razón social *" htmlFor="legal_name" fromRues={fromRues('legal_name')}>
        <input
          id="legal_name"
          type="text"
          value={s.legal_name}
          onChange={(e) => setS({ ...s, legal_name: e.target.value })}
          className={inputClsFor(fromRues('legal_name'))}
          placeholder="ACME S.A.S."
          disabled={fromRues('legal_name')}
        />
      </Field>

      <Field
        label="Nombre comercial (opcional)"
        htmlFor="trade_name"
        fromRues={fromRues('trade_name')}
      >
        <input
          id="trade_name"
          type="text"
          value={s.trade_name}
          onChange={(e) => setS({ ...s, trade_name: e.target.value })}
          className={inputClsFor(fromRues('trade_name'))}
          placeholder="ACME"
          disabled={fromRues('trade_name')}
        />
      </Field>

      {/* DV — SIEMPRE disabled. Calculado por algoritmo DIAN en backend.
          El usuario nunca lo escribe — es matemática del NIT, no input. */}
      <Field
        label="Dígito de verificación NIT"
        htmlFor="nit_dv"
        fromRues
        badgeLabel="Calculado (algoritmo DIAN)"
      >
        <input
          id="nit_dv"
          type="text"
          value={s.nit_verification_digit}
          readOnly
          disabled
          className={`${inputClsDisabled} w-20`}
        />
        <p className="mt-1 text-xs text-fg-muted">
          Se calcula a partir de tu NIT con el algoritmo DIAN. No es editable.
        </p>
      </Field>

      <Field label="Dirección *" htmlFor="address" fromRues={fromRues('address')}>
        <input
          id="address"
          type="text"
          value={s.address}
          onChange={(e) => setS({ ...s, address: e.target.value })}
          className={inputClsFor(fromRues('address'))}
          placeholder="Cra 7 # 71-21"
          disabled={fromRues('address')}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Ciudad *" htmlFor="city" fromRues={fromRues('municipality')}>
          <input
            id="city"
            type="text"
            value={s.city}
            onChange={(e) => setS({ ...s, city: e.target.value })}
            className={inputClsFor(fromRues('municipality'))}
            placeholder="Bogotá"
            disabled={fromRues('municipality')}
          />
        </Field>

        <Field
          label="Departamento *"
          htmlFor="department"
          fromRues={fromRues('department')}
        >
          {fromRues('department') ? (
            <input
              id="department"
              type="text"
              value={s.department}
              readOnly
              disabled
              className={inputClsDisabled}
            />
          ) : (
            <select
              id="department"
              value={s.department}
              onChange={(e) => setS({ ...s, department: e.target.value })}
              className={inputCls}
            >
              <option value="">Selecciona...</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      <Field
        label="Código CIIU principal (4-6 dígitos) *"
        htmlFor="ciiu"
        fromRues={fromRues('ciiu')}
      >
        <input
          id="ciiu"
          type="text"
          maxLength={6}
          value={s.ciiu}
          onChange={(e) => setS({ ...s, ciiu: e.target.value.replace(/\D/g, '') })}
          className={`${inputClsFor(fromRues('ciiu'))} w-32`}
          placeholder="6920"
          disabled={fromRues('ciiu')}
        />
        <p className="mt-1 text-xs text-fg-muted">
          Tu actividad económica DIAN. Ej: 6920 = contabilidad, 4711 = comercio.
        </p>
      </Field>
    </div>
  )
}

// ─── Step 2: Datos fiscales ─────────────────────────────────────────

function StepFiscal({
  s,
  setS,
}: {
  s: WizardState
  setS: React.Dispatch<React.SetStateAction<WizardState>>
}) {
  const toggle = (code: string) => {
    const has = s.fiscal_responsibilities.includes(code)
    const next = has
      ? s.fiscal_responsibilities.filter((c) => c !== code)
      : [...s.fiscal_responsibilities, code]
    setS({ ...s, fiscal_responsibilities: next })
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Datos fiscales</h2>
      <p className="text-sm text-fg-secondary">
        Cómo te clasifica DIAN. Si tienes dudas, consulta a tu contador.
      </p>

      <div>
        <p className="text-sm font-medium">Responsabilidades fiscales *</p>
        <p className="text-xs text-fg-muted">Marca todas las que apliquen.</p>
        <p className="mt-1 text-xs text-fg-muted">
          Si eres responsable de IVA en régimen ordinario y no tienes ninguna
          calidad especial (gran contribuyente, autorretenedor, agente de
          retención, RST), marca{' '}
          <span className="font-mono">R-99-PN</span> — el régimen (48) ya
          declara que eres responsable de IVA.
        </p>
        <div className="mt-2 space-y-2">
          {FISCAL_RESPONSIBILITIES.map((r) => (
            <label
              key={r.code}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-bg-app px-3 py-2 hover:bg-bg-elevated"
            >
              <input
                type="checkbox"
                checked={s.fiscal_responsibilities.includes(r.code)}
                onChange={() => toggle(r.code)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-mono text-xs text-fg-muted">{r.code}</span>{' '}
                {r.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <Field label="Régimen tributario *" htmlFor="tax_regime">
        <select
          id="tax_regime"
          value={s.tax_regime}
          onChange={(e) => setS({ ...s, tax_regime: e.target.value })}
          className={inputCls}
        >
          <option value="">Selecciona...</option>
          {TAX_REGIMES.map((r) => (
            <option key={r.code} value={r.code}>
              {r.code} — {r.label}
            </option>
          ))}
        </select>
      </Field>
    </div>
  )
}

// ─── Step 3: Cert ECD ──────────────────────────────────────────────

function StepCert({
  s,
  setS,
  activeOrgId,
}: {
  s: WizardState
  setS: React.Dispatch<React.SetStateAction<WizardState>>
  activeOrgId: string
}) {
  const [file, setFile] = useState<File | null>(null)
  const [pwd, setPwd] = useState('')
  const [uploading, setUploading] = useState(false)

  const handleUpload = async () => {
    if (!file || !pwd) {
      toast.error('Selecciona el .p12 e ingresa la contraseña')
      return
    }
    setUploading(true)
    try {
      await certApi.upload(activeOrgId, file, pwd)
      toast.success('Certificado subido. Validación issuer + expiración OK.')
      setS({ ...s, cert_uploaded: true })
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } } }
      toast.error(
        `No se pudo subir: ${
          e?.response?.data?.message ?? e?.response?.data?.error ?? 'error'
        }`,
      )
    } finally {
      setUploading(false)
      setPwd('') // Limpia el password del DOM.
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Certificado de firma DIAN</h2>
      <p className="text-sm text-fg-secondary">
        Cada factura debe firmarse digitalmente con cert ECD autorizado por DIAN.
      </p>

      <div className="space-y-3">
        <label
          className={`block cursor-pointer rounded-xl border-2 p-4 transition-colors ${
            s.cert_choice === 'mandate'
              ? 'border-brand-600 bg-brand-50/50'
              : 'border-border bg-bg-app hover:bg-bg-elevated'
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="cert_choice"
              value="mandate"
              checked={s.cert_choice === 'mandate'}
              onChange={() => setS({ ...s, cert_choice: 'mandate' })}
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-brand-600" />
                <p className="font-semibold">Mandato Taxor (recomendado)</p>
              </div>
              <p className="mt-1 text-sm text-fg-secondary">
                Taxor firma con su cert ECD bajo Contrato de Mandato (DIAN-reconocido).
                Cero trámite ECD para ti — sin Andes, sin Certicámara, sin renovación.
              </p>
              <p className="mt-1 text-xs text-fg-muted">
                Aceptaste el Contrato de Mandato al iniciar sesión.
              </p>
            </div>
          </div>
        </label>

        <label
          className={`block cursor-pointer rounded-xl border-2 p-4 transition-colors ${
            s.cert_choice === 'byo'
              ? 'border-brand-600 bg-brand-50/50'
              : 'border-border bg-bg-app hover:bg-bg-elevated'
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="cert_choice"
              value="byo"
              checked={s.cert_choice === 'byo'}
              onChange={() => setS({ ...s, cert_choice: 'byo' })}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-fg-secondary" />
                <p className="font-semibold">Trae tu propio certificado (BYO)</p>
              </div>
              <p className="mt-1 text-sm text-fg-secondary">
                Sube tu .p12 emitido por una ECD COL (Andes / Certicámara / GSE /
                Camerfirma / Thomas-Signe). Solo si compliance interno te lo exige.
              </p>

              {s.cert_choice === 'byo' && (
                <div className="mt-4 space-y-3 rounded-md bg-bg-app p-3">
                  <div>
                    <label className="text-xs font-medium" htmlFor="p12">
                      Archivo .p12
                    </label>
                    <input
                      id="p12"
                      type="file"
                      accept=".p12,.pfx"
                      onChange={(e) => {
                        setFile(e.target.files?.[0] ?? null)
                        setS({ ...s, cert_uploaded: false })
                      }}
                      className="mt-1 block w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium" htmlFor="p12_pwd">
                      Contraseña del .p12
                    </label>
                    <input
                      id="p12_pwd"
                      type="password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      className={inputCls}
                      autoComplete="off"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={uploading || s.cert_uploaded}
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo...
                      </>
                    ) : s.cert_uploaded ? (
                      <>
                        <Check className="h-3.5 w-3.5" /> Subido OK
                      </>
                    ) : (
                      <>
                        <Upload className="h-3.5 w-3.5" /> Subir + validar
                      </>
                    )}
                  </button>
                  <p className="text-xs text-fg-muted">
                    El .p12 se valida (issuer whitelist ECD COL + expiración &gt; 30d) y
                    se cifra at-rest con HKDF per-tenant antes de persistir.
                  </p>
                </div>
              )}
            </div>
          </div>
        </label>
      </div>
    </div>
  )
}

// ─── Step 4: Resolución DIAN ───────────────────────────────────────

// applyRangeToState llena los campos de resolución del wizard con un
// rango detectado vía DIAN (sin clave técnica — esa es server-side).
function applyRangeToState(prev: WizardState, r: NumberingRange): WizardState {
  return {
    ...prev,
    resolution_number: r.resolution_number,
    resolution_prefix: r.prefix,
    resolution_from: String(r.from_number),
    resolution_to: String(r.to_number),
    resolution_valid_from: r.valid_date_from,
    resolution_valid_to: r.valid_date_to,
  }
}

function StepResolution({
  s,
  setS,
  ranges,
  rangesResolved,
  rangesError,
}: {
  s: WizardState
  setS: React.Dispatch<React.SetStateAction<WizardState>>
  ranges?: NumberingRangesResult
  /** true cuando la query de rangos ya terminó (éxito o error). */
  rangesResolved: boolean
  /** true si la consulta de rangos a la DIAN falló. */
  rangesError: boolean
}) {
  const detected = ranges?.ranges ?? []

  // La consulta de rangos falló o volvió vacía (fuera del ambiente de
  // habilitación, donde la resolución de prueba del SIE siempre existe).
  // El usuario puede capturar manualmente, pero el guardado final igual
  // necesita que Taxor resuelva la clave técnica ante la DIAN — mejor
  // avisarlo AQUÍ y no recién en el submit del paso 5.
  const rangesUnavailable =
    rangesResolved &&
    (rangesError || (detected.length === 0 && ranges?.source !== 'habilitacion'))

  // Re-entrada al wizard con draft: la resolución guardada puede haber
  // dejado de existir entre los rangos vigentes detectados en la DIAN.
  // No pisamos los datos del usuario — solo avisamos y ofrecemos aplicar
  // el mejor rango detectado con un clic.
  const staleSelection =
    detected.length > 0 &&
    !!s.resolution_number &&
    !detected.some(
      (r) => r.resolution_number === s.resolution_number && r.prefix === s.resolution_prefix,
    )
  const bestDetected = detected.find((r) => r.is_active) ?? detected[0]

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Resolución DIAN de facturación</h2>
      <p className="text-sm text-fg-secondary">
        Tu rango de numeración autorizado. Si Taxor lo detecta en la DIAN,
        se aplica solo; también puedes capturarlo desde tu resolución de
        muisca.dian.gov.co.
      </p>

      {/* La DIAN no devolvió rangos (o la consulta falló): captura manual
          posible, pero el guardado depende de resolver la clave técnica. */}
      {rangesUnavailable && (
        <div className="rounded-xl border border-warning/40 bg-warning-tint p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-none text-warning-fg" />
            <div className="text-sm">
              <p className="font-semibold text-warning-fg">
                Taxor aún no puede consultar tus rangos a la DIAN
              </p>
              <p className="mt-1 text-xs text-fg-secondary">
                Puedes capturar tu resolución manualmente en los campos de
                abajo. Ten en cuenta que al guardar (paso 5) Taxor debe
                resolver la clave técnica de tu rango ante la DIAN — si ese
                guardado falla, contacta a soporte.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rangos detectados vía Taxor como PT ante DIAN. */}
      {detected.length > 0 && (
        <div className="rounded-xl border border-brand-500/30 bg-brand-50/50 p-4 dark:bg-brand-950/20">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand-700 dark:text-brand-300">
            <ShieldCheck className="h-4 w-4" />
            {ranges?.source === 'habilitacion'
              ? 'Resolución del ambiente de pruebas DIAN (habilitación)'
              : 'Resoluciones detectadas en la DIAN'}
          </div>
          <p className="mt-1 text-xs text-fg-secondary">
            Taxor, como tu proveedor tecnológico, consultó estos rangos
            directamente a la DIAN. Selecciona uno y no digitas nada.
          </p>
          <ul className="mt-3 space-y-2">
            {detected.map((r) => {
              const selected =
                s.resolution_number === r.resolution_number &&
                s.resolution_prefix === r.prefix
              return (
                <li
                  key={`${r.resolution_number}-${r.prefix}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-bg-surface px-3 py-2"
                >
                  <div className="text-xs">
                    <span className="font-mono font-semibold">
                      {r.prefix} {r.from_number.toLocaleString('es-CO')}–
                      {r.to_number.toLocaleString('es-CO')}
                    </span>{' '}
                    <span className="text-fg-muted">
                      · Res. {r.resolution_number} · vence {r.valid_date_to}
                    </span>
                    {!r.is_active && (
                      <span className="ml-2 rounded-full bg-error-tint px-2 py-0.5 text-[10px] font-semibold text-error-fg">
                        vencida
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setS((prev) => applyRangeToState(prev, r))}
                    disabled={selected}
                    className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 text-xs font-medium text-brand-600 hover:bg-brand-500/20 disabled:cursor-default disabled:opacity-60"
                  >
                    {selected ? 'En uso ✓' : 'Usar este rango'}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Draft stale: la resolución cargada ya no aparece entre los rangos
          vigentes. No se sobreescribe nada sin acción explícita del user. */}
      {staleSelection && bestDetected && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning-tint p-3">
          <div className="flex items-start gap-2 text-xs text-warning-fg">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <span>
              La resolución cargada no coincide con los rangos vigentes
              detectados en la DIAN.
            </span>
          </div>
          <button
            type="button"
            onClick={() => setS((prev) => applyRangeToState(prev, bestDetected))}
            className="rounded-md border border-warning/40 bg-bg-surface px-2.5 py-1 text-xs font-medium text-warning-fg hover:bg-bg-elevated"
          >
            Aplicar rango detectado
          </button>
        </div>
      )}

      <Field label="Número de resolución *" htmlFor="resolution_number">
        <input
          id="resolution_number"
          type="text"
          value={s.resolution_number}
          onChange={(e) => setS({ ...s, resolution_number: e.target.value })}
          className={inputCls}
          placeholder="18760000001"
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Prefijo *" htmlFor="resolution_prefix">
          <input
            id="resolution_prefix"
            type="text"
            maxLength={4}
            value={s.resolution_prefix}
            onChange={(e) => setS({ ...s, resolution_prefix: e.target.value.toUpperCase() })}
            className={inputCls}
            placeholder="FE"
          />
        </Field>
        <Field label="Desde (consecutivo) *" htmlFor="resolution_from">
          <input
            id="resolution_from"
            type="text"
            inputMode="numeric"
            value={s.resolution_from}
            onChange={(e) =>
              setS({ ...s, resolution_from: e.target.value.replace(/\D/g, '') })
            }
            className={inputCls}
            placeholder="1"
          />
        </Field>
        <Field label="Hasta (consecutivo) *" htmlFor="resolution_to">
          <input
            id="resolution_to"
            type="text"
            inputMode="numeric"
            value={s.resolution_to}
            onChange={(e) =>
              setS({ ...s, resolution_to: e.target.value.replace(/\D/g, '') })
            }
            className={inputCls}
            placeholder="5000"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Vigente desde *" htmlFor="resolution_valid_from">
          <input
            id="resolution_valid_from"
            type="date"
            value={s.resolution_valid_from}
            onChange={(e) => setS({ ...s, resolution_valid_from: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Vigente hasta *" htmlFor="resolution_valid_to">
          <input
            id="resolution_valid_to"
            type="date"
            value={s.resolution_valid_to}
            onChange={(e) => setS({ ...s, resolution_valid_to: e.target.value })}
            className={inputCls}
          />
        </Field>
      </div>

      {/* La clave técnica es Taxor-managed — NUNCA se pide al usuario.
          DIAN la emite por rango y solo el PT puede consultarla
          (GetNumberingRange, Res. 227/2025 art. 1.5.1.5.1.1 num. 4.4). */}
      <div className="flex items-start gap-3 rounded-md border border-border bg-bg-app p-3">
        <KeyRound className="mt-0.5 h-4 w-4 flex-none text-brand-600" />
        <div className="text-xs">
          <p className="font-semibold text-fg-primary">
            Clave técnica: la gestiona Taxor
          </p>
          <p className="mt-0.5 text-fg-secondary">
            La clave técnica de tu rango (necesaria para el CUFE de cada
            factura) la emite la DIAN y solo tu proveedor tecnológico puede
            consultarla. Taxor la obtiene y la custodia por ti — no tienes
            que digitarla.
          </p>
        </div>
      </div>

      <Field label="Software ID DIAN (opcional)" htmlFor="software_id">
        <input
          id="software_id"
          type="text"
          value={s.software_id}
          onChange={(e) => setS({ ...s, software_id: e.target.value })}
          className={inputCls}
          placeholder="fa8c5872-ec98-..."
        />
        <p className="mt-1 text-xs text-fg-muted">
          Si DIAN te asignó un Software ID propio. Por defecto usa el de Taxor.
        </p>
      </Field>
    </div>
  )
}

// ─── Step 5: Confirmación ───────────────────────────────────────────

function StepConfirm({ s }: { s: WizardState }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Confirmar y activar</h2>
      <p className="text-sm text-fg-secondary">
        Revisa el resumen. Al confirmar, tu facturador queda activo para emitir.
      </p>

      <dl className="grid gap-3 rounded-md border border-border bg-bg-app p-4 text-sm">
        <Row label="Razón social" value={s.legal_name} />
        <Row label="NIT-DV" value={s.nit_verification_digit} mono />
        <Row label="Dirección" value={`${s.address}, ${s.city} (${s.department})`} />
        <Row label="CIIU" value={s.ciiu} mono />
        <Row label="Régimen" value={s.tax_regime} mono />
        <Row label="Responsabilidades" value={s.fiscal_responsibilities.join(', ') || '—'} mono />
        <Row label="Firma" value={s.cert_choice === 'mandate' ? 'Mandato Taxor' : 'Cert propio (BYO)'} />
        <Row
          label="Resolución"
          value={`${s.resolution_prefix} (${s.resolution_from}-${s.resolution_to})`}
          mono
        />
        <Row label="Vigencia" value={`${s.resolution_valid_from} → ${s.resolution_valid_to}`} mono />
      </dl>

      <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4 text-xs text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
        <strong>Importante:</strong> Antes de emitir en producción, DIAN exige
        certificación previa (10 facturas + 1 NC + 1 ND aceptadas en habilitación).
        Tu primer set automáticamente se envía al ambiente de pruebas DIAN.
      </div>
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={`col-span-2 ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</dd>
    </div>
  )
}

// ─── Helpers UI ─────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  children,
  fromRues = false,
  badgeLabel,
}: {
  label: string
  htmlFor: string
  children: ReactNode
  /** Si true, agrega badge "Desde RUES" (o el badgeLabel override). */
  fromRues?: boolean
  /** Override del texto del badge. Útil para casos especiales como
   *  el DV ("Calculado") que no viene de RUES propiamente sino del
   *  algoritmo DIAN. */
  badgeLabel?: string
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <label htmlFor={htmlFor} className="block text-sm font-medium">
          {label}
        </label>
        {fromRues && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
            title="Este dato viene de Cámara de Comercio vía RUES. Para editarlo, actualízalo en Cámara."
          >
            <Building2 className="h-3 w-3" />
            {badgeLabel ?? 'Desde RUES'}
          </span>
        )}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  )
}

const inputCls =
  'block w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary placeholder-fg-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

// Variante disabled con look distinto (bg más opaco, cursor not-allowed).
// Para campos que vienen de tax_account.organizations y NO se editan en el wizard.
const inputClsDisabled =
  'block w-full cursor-not-allowed rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg-secondary placeholder-fg-muted opacity-90'

function inputClsFor(disabled: boolean): string {
  return disabled ? inputClsDisabled : inputCls
}
